import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Rating,
  Collapse,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Pagination,
  Stack
} from "@mui/material";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import axios from 'axios';
import { localAxiosGet } from '../../../Axios/axios';

const PastApparaisalOfEmployee = ({ selectedemployeeId, open, onClose, employeeName }) => {
  const [pastAppraisalDetails, setPastAppraisalDetails] = useState([]);
  const [expandedAppraisals, setExpandedAppraisals] = useState({});
  const [selectedYear, setSelectedYear] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;

  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // Calculate pagination
  const totalPages = Math.ceil(pastAppraisalDetails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAppraisals = pastAppraisalDetails.slice(startIndex, endIndex);

  const handleToggleExpand = (appraisalId) => {
    setExpandedAppraisals(prev => ({
      ...prev,
      [appraisalId]: !prev[appraisalId]
    }));
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Reset expanded states when changing pages
    setExpandedAppraisals({});
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setCurrentPage(1); // Reset to first page when year changes
    setExpandedAppraisals({}); // Reset expanded states
  };

  // Fetch past appraisals when modal opens
  useEffect(() => {
    if (open && selectedemployeeId && selectedYear) {
      const fetchPastAppraisals = async () => {
        try {
          const response = await axios(
            localAxiosGet(`/performancemanagement/getRatingsAndFeedback?emp_id=${selectedemployeeId}&appraisal_year=${selectedYear}`)
          );
          
          // Check if result exists and wrap single object in array
          if (response.data.result) {
            setPastAppraisalDetails([response.data.result]);
          } else {
            setPastAppraisalDetails([]);
          }
          
        } catch (err) {
          console.error("Error fetching past appraisals:", err);
          setPastAppraisalDetails([]);
        }
      };
      fetchPastAppraisals();
    }
  }, [open, selectedemployeeId, selectedYear]);

  // Reset pagination when modal opens
  useEffect(() => {
    if (open) {
      setCurrentPage(1);
      setExpandedAppraisals({});
    }
  }, [open]);

  const handleCloseModal = () =>{
    onClose();
    setSelectedYear('')
  }

  const getStatusColor = (status) => {
    const statusColors = {
      completed: 'success',
      pending: 'warning',
      draft: 'default',
      acknowledged: 'info'
    };
    return statusColors[status] || 'default';
  };

  return (
    <div>
      <Modal open={open} onClose={handleCloseModal}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 900,
            maxHeight: '85%',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            overflowY: 'auto',
          }}
        >
          <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
            Past Appraisals for {employeeName}
          </Typography>

          {/* Appraisal Year Dropdown */}
          <Box sx={{ mt: 3, mb: 2, maxWidth:'200px' }}>
            <FormControl fullWidth>
              <InputLabel id="appraisal-year-label">Appraisal Year</InputLabel>
              <Select
                labelId="appraisal-year-label"
                value={selectedYear}
                id='appraisal-year-label'
                label="Appraisal Year"
                onChange={handleYearChange}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ p: 3 }}>
            {pastAppraisalDetails && pastAppraisalDetails.length > 0 ? (
              <>
                {/* Appraisals Grid */}
                <Grid container spacing={3}>
                  {currentAppraisals.map((appraisal) => (
                    <Grid item xs={12} sm={6} lg={4} key={appraisal.id}>
                      <Card sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 3,
                        borderRadius: 2,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        }
                      }}>
                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                          {/* Appraisal Period & Year */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                                {appraisal.appraisal_period} {appraisal.appraisal_year}
                              </Typography>
                              <Chip
                                label="Completed"
                                color={getStatusColor('completed')}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                            <Avatar sx={{
                              bgcolor: 'primary.main',
                              width: 40,
                              height: 40,
                              fontSize: '0.9rem'
                            }}>
                              {appraisal.appraisal_period?.charAt(0)}
                            </Avatar>
                          </Box>

                          {/* Ratings Summary */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                              Overall Rating:
                            </Typography>
                            {appraisal.ratings && appraisal.ratings.length > 0 ? (
                              <>
                                <Rating
                                  value={appraisal.ratings.reduce((sum, r) => sum + r.rating, 0) / appraisal.ratings.length}
                                  readOnly
                                  precision={0.1}
                                  size="small"
                                />
                                <Typography variant="body2" sx={{ ml: 1, display: 'inline', fontWeight: 'bold' }}>
                                  ({(appraisal.ratings.reduce((sum, r) => sum + r.rating, 0) / appraisal.ratings.length).toFixed(1)})
                                </Typography>
                              </>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No ratings available
                              </Typography>
                            )}
                          </Box>

                          {/* Strengths Preview */}
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            <Box component="span" sx={{ fontWeight: 'bold' }}>Strengths: </Box>
                            {appraisal.strengths && appraisal.strengths.length > 100
                              ? `${appraisal.strengths.substring(0, 100)}...`
                              : appraisal.strengths || 'Not specified'
                            }
                          </Typography>

                          {/* Expandable Content */}
                          <Collapse in={expandedAppraisals[appraisal.id]} collapsedSize={60}>
                            {/* Areas of Improvement */}
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              <Box component="span" sx={{ fontWeight: 'bold' }}>Improvement Areas: </Box>
                              {appraisal.area_of_improvement || 'Not specified'}
                            </Typography>

                            {/* Goals for Next Period */}
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              <Box component="span" sx={{ fontWeight: 'bold' }}>Future Goals: </Box>
                              {appraisal.goals_for_next_period || 'Not specified'}
                            </Typography>

                            {/* Category Ratings */}
                            {appraisal.ratings && appraisal.ratings.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                                  Category Ratings:
                                </Typography>
                                {appraisal.ratings.map((rating) => (
                                  <Box key={rating.id} sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1,
                                    p: 1,
                                    backgroundColor: 'grey.50',
                                    borderRadius: 1
                                  }}>
                                    <Typography variant="body2">
                                      {rating.category_name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Rating value={rating.rating} readOnly precision={0.5} size="small" />
                                      <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold', minWidth: '30px' }}>
                                        {rating.rating.toFixed(1)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Collapse>

                          {/* Expand/Collapse Button */}
                          <Box sx={{ textAlign: 'center', mt: 2 }}>
                            <Button
                              size="small"
                              onClick={() => handleToggleExpand(appraisal.id)}
                              endIcon={expandedAppraisals[appraisal.id] ? <ExpandLess /> : <ExpandMore />}
                              sx={{ textTransform: 'none' }}
                            >
                              {expandedAppraisals[appraisal.id] ? 'Show Less' : 'Show More'}
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Stack spacing={2}>
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Showing {startIndex + 1}-{Math.min(endIndex, pastAppraisalDetails.length)} of {pastAppraisalDetails.length} appraisals
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
                flexDirection: 'column',
                textAlign: 'center'
              }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No Past Appraisals Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no past appraisals available for this employee.
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 2, textAlign: 'right' }}>
            <Button variant="outlined" onClick={handleCloseModal}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default PastApparaisalOfEmployee;